import React from 'react';
import { StaticQuery, graphql } from 'gatsby';
import { withPreview } from 'gatsby-source-prismic-graphql';
import { AlternateLanguage, PageContextType, Edges } from 'types';
import { CookieConsent, CookieConsentData } from 'components/CookieConsent';
import { MainFooter, MainFooterData } from 'components/MainFooter';
import { MainNavigation } from 'components/MainNavigation';
import { MainNavigationData } from 'components/MainNavigation/types';
import './Layout.scss';
import { LinkFragments, MetaFragment } from 'templates/fragments.gql';
import { MainNavigationFragment } from 'components/MainNavigation/MainNavigation.gql';
import { MainFooterFragment } from 'components/MainFooter/MainFooter.gql';
import { CookieConsentFragment } from 'components/CookieConsent/CookieConsent.gql';
import { isPreviewActive } from 'helpers';
import { PrismicLinkFieldType, PrismicImageField } from '../Slices/types';

interface LayoutProps {
    lang: string;
    homePage: string | null;
    pageContext: PageContextType;
    alternateLanguages: AlternateLanguage[];
    hasGivenCookieConsent: boolean;
    onAgreeToCookieConsent: () => void;
    options: {
        show_navigation_header: boolean;
        header_type: string;
        partner_logo: PrismicImageField;
        partner_url: PrismicLinkFieldType;
        show_footer: boolean;
    };
}

export const getDataForLang = <T extends Record<string, unknown>>(all: Edges<T>, lang: string): T | null => {
    if (!all || !all.edges || !all.edges.length) {
        return null;
    }

    const findEdge = (language: string) => {
        return all.edges.find((e: any) => e.node._meta.lang === language);
    };

    const edge = findEdge(lang) || findEdge('en-us');

    return edge ? edge.node : null;
};

type LayoutData = {
    prismic: {
        allMain_navigations: Edges<MainNavigationData>;
        allMain_footers: Edges<MainFooterData>;
        allCookie_consents: Edges<CookieConsentData>;
    };
};

const LayoutQuery = graphql`
    query LayoutQuery {
        prismic {
            allMain_navigations {
                edges {
                    node {
                        ...PrismicMainNavigationFragment
                    }
                }
            }
            allMain_footers {
                edges {
                    node {
                        ...PrismicMainFooterFragment
                    }
                }
            }
            allCookie_consents {
                edges {
                    node {
                        ...PrismicCookieConsentFragment
                    }
                }
            }
        }
    }
`;

export const Layout: React.FC<LayoutProps> = ({
    children,
    lang,
    alternateLanguages,
    options,
    pageContext,
    homePage,
    hasGivenCookieConsent,
    onAgreeToCookieConsent,
}) => {
    const render = ({ prismic: { allMain_navigations, allMain_footers, allCookie_consents } }: LayoutData) => {
        const mainNavData = options.show_navigation_header && getDataForLang(allMain_navigations, lang);
        const mainFooterData = options.show_footer && getDataForLang(allMain_footers, lang);
        const cookieConsentData = !hasGivenCookieConsent && getDataForLang(allCookie_consents, lang);
        return (
            <div className="Site">
                {mainNavData && (
                    <MainNavigation
                        mainNavigationData={mainNavData}
                        pageContext={pageContext}
                        homePage={homePage}
                        headerType={options.header_type}
                        partnerLogo={options.partner_logo}
                        partnerUrl={options.partner_url}
                    />
                )}
                <div className="Site-body">{children}</div>
                {mainFooterData && (
                    <MainFooter mainFooterData={mainFooterData} alternateLanguages={alternateLanguages} lang={lang} />
                )}
                {cookieConsentData && (
                    <CookieConsent data={cookieConsentData} onAgreeToCookieConsent={onAgreeToCookieConsent} />
                )}
            </div>
        );
    };

    return (
        <StaticQuery
            query={`${LayoutQuery}`}
            render={
                // Use withPreview ONLY when preview is active, as it makes the navigation flicker
                isPreviewActive()
                    ? (withPreview(render, LayoutQuery, [
                          MainNavigationFragment,
                          MainFooterFragment,
                          CookieConsentFragment,
                          MetaFragment,
                          LinkFragments,
                      ]) as any)
                    : render
            }
        />
    );
};
